import {Injectable, NgZone} from '@angular/core';
import {AuthService} from '@auth0/auth0-angular';
import {HttpClient} from '@angular/common/http';
import {Router} from '@angular/router';
import {environment} from '../../../../environments/environment';
import {BehaviorSubject, firstValueFrom, Observable} from 'rxjs';
import {ApiEndPoint} from '../../utility/api-end-point';
import {AppEnumConstants} from '../../enums/app-enum-constants';
import {NotificationService} from '../notification/notification.service';
import {CommonUtility} from '../../utility/common-utility';
import {HttpResponseMessage} from '../../utility/http-response-message';
import {AppConstant} from "../../utility/app-constant";

@Injectable({
  providedIn: 'root'
})
export class AuthOktaService {

  constructor(public auth: AuthService, private httpClient: HttpClient, private zone: NgZone,
              private notificationService: NotificationService, private router: Router) {
  }

  public userDetail;
  private organizationId: any | null = null;
  private isLoadingOrgId = false;
  private commonUtility = new CommonUtility();
  public logoutSubject = new BehaviorSubject(null);
  public stopApiCall = false;

  private orgIdPromise: Promise<string | null> | null = null;

  public async signIn(orgId?): Promise<void> {
    orgId = this.organizationId;
    const options: any = {
      authorizationParams: {}
    };

    // Add org if provided
    if (orgId) {
      options.authorizationParams.organization = orgId;
    }
    await this.auth.loginWithRedirect(options);
  }

  public async signOut(data?: any): Promise<void> {
    const options = {
      logoutParams: {
        returnTo: environment.auth.authorizationParams.redirect_uri,
      }, ...data
    };
    await this.auth.logout(options);
  }

  /**
   * Synchronously check if organization ID is available (for MFA check)
   * Returns the cached organization ID or null
   */
  public hasOrganizationId(): string | null {
    // Return the cached value directly (no API call)
    return this.organizationId || null;
  }

  /**
   * Check if current route is a public route that should not trigger organization ID API call
   * @returns true if current route is a public route
   */
  private isPublicRoute(): boolean {
    // Use window.location.pathname for reliable URL detection during early app initialization
    const currentPath = window.location.pathname;
    return AppConstant.PUBLIC_ROUTES.some(route => currentPath.startsWith(route));
  }

  async getOrganizationId(): Promise<string | null> {
    // 1. Memory cache check
    if (this.organizationId) {
      return this.organizationId;
    }

    // Check if current route is a public route - skip API call to avoid redirect
    if (this.isPublicRoute()) {
      return null;
    }

    if (this.stopApiCall){
      return;
    }

    // 2. Race condition protection: If a request is already in flight, return that promise
    if (this.orgIdPromise) {
      return this.orgIdPromise;
    }

    // 3. LocalStorage check
    const cachedOrgId = localStorage.getItem(AppEnumConstants.ORGANIZATION_ID);

    // If we have a valid non-empty string, use it
    if (cachedOrgId) {
      this.organizationId = cachedOrgId;
      return cachedOrgId;
    }

    // 4. API Fetch (Wrapped in a promise tracker to collapse multiple calls)
    this.orgIdPromise = this.fetchAndProcessOrganization();
    return this.orgIdPromise;
  }

  private async fetchAndProcessOrganization(): Promise<string | null> {
    const domainName = environment.domainName.replace('.papertrl.com', '');

    try {
      const response = await this.getOrganization(domainName);
      return response;
    } catch (error) {
      this.handleOrgError(error, domainName);
      this.organizationId = false; // Enter legacy mode for session
      return null;
    } finally {
      this.orgIdPromise = null; // Reset promise tracker
    }
  }

  async getOrganization(domainName: string): Promise<string | null> {
    const response = await firstValueFrom(
      this.httpClient.get(`${ApiEndPoint.API_URL}/tenant_management/get_organization_id`, {
        params: {domainName},
        responseType: 'text'
      })
    );

    const cleanResponse = response?.trim();

    if (!cleanResponse || cleanResponse === 'false') {
      localStorage.removeItem(AppEnumConstants.ORGANIZATION_ID);
      this.organizationId = false;
      return null;
    }

    // Successful ID retrieval
    this.organizationId = cleanResponse;
    localStorage.setItem(AppEnumConstants.ORGANIZATION_ID, cleanResponse);

    // Trigger UI changes/Reload
    this.logoutSubject.next(true);
    setTimeout(() => window.location.reload(), 2000);

    return cleanResponse;
  }

  private handleOrgError(error: any, domainName: string): void {
    this.commonUtility.logErrorToSentry(error, {
      tags: {errorType: AppEnumConstants.AUTH0, errorGroup: AppEnumConstants.AUTH},
      extra: {domainName, timestamp: new Date().toISOString(), method: 'getOrganizationId', error}
    });
  }

  updateOrg(metaData) {
    const orgId = metaData.orgId;
    const metadata = metaData.metadata;
    return this.httpClient.post(ApiEndPoint.API_URL + '/api/update-org', {orgId, metadata});
  }

  addUsersOrg(metaData) {
    const orgId = metaData.orgId;
    const userIds = metaData.userIds;
    return this.httpClient.post(ApiEndPoint.API_URL + '/api/add-users-to-org', {orgId, userIds});
  }

  public getUsers() {
    return this.httpClient.get('http://localhost:8082/api/get-users', {observe: 'response'});
  }

  enableMfa(mfaData: any) {
    return this.httpClient.post(ApiEndPoint.API_URL + '/auth/mfa-ticket', {mfaData});
  }

  markMfaEnabled() {
    const userId = this.userDetail.sub;
    const metadata = {mfa_enabled: true};

    this.httpClient.post(ApiEndPoint.API_URL + '/api/update-user', {userId, metadata})
      .subscribe(() => {
        console.log('MFA enabled flag updated');
      });
  }

  getEnrollments() {
    const userId = this.userDetail.sub;
    const metadata = {mfa_enabled: true};

    this.httpClient.get(ApiEndPoint.API_URL + '/api/mfa/enrollments', {params: {userId}})
      .subscribe(() => {
        console.log('MFA enabled flag updated');
      });
  }

  getUserDetails() {
    return new Promise(resolve => this.auth.user$.subscribe(value => {
      this.userDetail = value;
      resolve(value);
    }));
  }

  listenForMfa(userId: string): Observable<any> {
    return new Observable(observer => {
      const eventSource = new EventSource(ApiEndPoint.API_URL + `/events?userId=${userId}`);
      eventSource.addEventListener('mfaEnabled', (event: any) => {
        this.zone.run(() => {
          observer.next(JSON.parse(event.data));
        });
      });

      eventSource.onerror = (error) => {
        console.error('SSE error', error);
        eventSource.close();
        observer.error(error);
      };

      return () => eventSource.close();
    });
  }

  /**
   * Send password reset email using Auth0's public database connection endpoint
   */
  resetPassword(username: any) {
    this.httpClient.post(ApiEndPoint.API_URL + `/tenant_management/reset-auth0-password`, null
      , {observe: 'response', params: {username}}).subscribe({
      next: (response) => {
        this.notificationService.successMessage(HttpResponseMessage.PASSWORD_RESET_LINK_SEND_SUCCESSFULLY);
      },
      error: (error) => {
        console.error('Password reset error', error);
      }
    });
  }

  /**
   * Send email verification using Auth0's public database connection endpoint
   * @param userEmail Optional email address to resend verification to
   */
  resendEmailVerification(userEmail?: string) {
    return this.httpClient.get(ApiEndPoint.API_URL + `/tenant_management/email-verification-resend`,
      {observe: 'response', params: {userEmail}});
  }
}
